// SPDX-License-Identifier: GPL-3.0-only
// OpenSaga Players_InitPositions, CheckResetBits party-script selection,
// TagCharacter/TagCode, Move_DROIDGENERIC and original companion SCP actions.
import { EnemyScriptRuntime } from './enemies.mjs';
import { sourceAtan2 } from './projectiles.mjs';

const point = value => Array.isArray(value) ? value.slice() : [value.x, value.y, value.z];
const position = actor => point(actor.root?.position ?? actor.position);
const distance2 = (a, b) => a.reduce((sum, v, i) => sum + (v - b[i]) ** 2, 0);
const flag = (values, name) => values[name] === true || values[name] === 'on';
export function sourceCharacterAbilities(config) {
  const values = config.values ?? config, protocol = flag(values, 'protocol'), astromech = flag(values, 'astromech'), genericDroid = flag(values, 'droid') && !flag(values, 'jedi');
  return { jedi: flag(values, 'jedi'), protocol, astromech, force: flag(values, 'jedi'),
    jump: !protocol && (!genericDroid || astromech) && !flag(values, 'no_jump') && (Number.isFinite(values.jump_speed) || astromech),
    doubleJump: !protocol && !astromech && !genericDroid && Number.isFinite(values.jump_2_speed),
    build: !protocol && !astromech && !genericDroid, melee: !protocol && !astromech && !genericDroid,
    blaster: !protocol && flag(values, 'blaster'), protocolPanel: protocol,
    // Untargetable is an AI target-selection flag, NOT damage immunity.
    aiTargetable: !flag(values, 'untargetable') };
}

export function storyPartyStarts(roster, startPoints, { doorEntry = false } = {}) {
  if (!Array.isArray(roster) || roster.length > 8 || !Array.isArray(startPoints) || startPoints.length < (doorEntry ? 6 : 2))
    throw new Error('Missing original party start spline pairs');
  const points = startPoints.map(point);
  if (points.some(p => p.length !== 3 || !p.every(Number.isFinite))) throw new Error('Invalid original party start point');
  let pair = doorEntry ? 2 : 0;
  return roster.map((item, index) => {
    const sourcePair = pair, origin = points[pair * 2], target = points[pair * 2 + 1];
    if (!target) throw new Error('Incomplete original party start pair');
    const angle = sourceAtan2(target[0] - origin[0], target[2] - origin[2]);
    pair = pair * 2 + 4 > points.length ? (doorEntry ? 2 : 0) : pair + 1;
    return { name: typeof item === 'string' ? item.toLowerCase() : item.id.toLowerCase(), playerIndex: index,
      position: origin.slice(), heading: (angle & 65535) * Math.PI / 32768, sourcePair, source: 'Players_InitPositions' };
  });
}

export function selectStoryPartyScript(name, abilities, levelScripts, globalScripts) {
  const category = abilities.jedi ? 'jedi' : abilities.blaster ? 'blaster' : 'noweapon';
  return levelScripts.get(name.toLowerCase()) ?? levelScripts.get(category) ?? levelScripts.get('party') ?? globalScripts.get('generalparty') ?? null;
}

// Ordinary story tagging transfers control/HP/coin packet/player slot and
// spawn protection, retaining both actors' authored physical positions.
// Companion movement is the same bounded clear-segment adapter used by the
// browser enemies, not a replacement claim for native pathfinding.
export function createStoryParty({ members, controlledIndex = 0, ai, levelScripts = new Map(), globalScripts = new Map(),
  messages = new Map(), missionMode = false, cutsceneLocked = () => false, onTransfer = () => {},
  obstacleAtEnd = () => undefined, gotOpponent = () => false, helpWithTriggers = () => false,
  getFloor, getLineOfSight } = {}) {
  if (!Array.isArray(members) || !members.length || members.length > 8 || !Number.isInteger(controlledIndex) || !members[controlledIndex])
    throw new Error('Invalid original story party');
  const report = { blocked: [], limitations: ['Ordinary story party only; vehicles, takeovers, co-op joining, Vader special health and tag transfer camera effects are not implemented.',
    'Companion movement supports authored FollowPlayer/GoToLocator on confirmed clear terrain segments; native routing, jumping and combat/help-trigger actions remain incomplete.'] };
  const seen = new Set(), note = (member, reason) => { const key = `${member.name}:${reason}`; if (!seen.has(key)) { seen.add(key); report.blocked.push({ name: member.name, reason }); } };
  const slots = members.map(({ name, actor }, index) => {
    if (typeof name !== 'string' || !actor?.root || !actor.config?.values) throw new Error('Invalid original party member');
    actor.playerControlled = index === controlledIndex; actor.playerIndex = index; actor.tagCooldown = 0;
    return { name: name.toLowerCase(), actor, abilities: sourceCharacterAbilities(actor.config), intent: null, velocity: 0, actionData: null };
  });
  if (new Set(slots.map(member => member.name)).size !== slots.length) throw new Error('Duplicate original party member');
  const authoredMembers = slots.slice(); let controlled = slots[controlledIndex];
  const scripts = new Map([...globalScripts, ...levelScripts]);
  const findLocator = name => ai?.locators?.find(locator => locator.name === name?.toLowerCase());
  const setPosition = (actor, p) => { if (actor.root.position.fromArray) actor.root.position.fromArray(p); else Object.assign(actor.root.position, { x: p[0], y: p[1], z: p[2] }); };
  for (const member of authoredMembers) {
    const { actor } = member, script = selectStoryPartyScript(member.name, member.abilities, levelScripts, globalScripts);
    if (!script) { note(member, 'Original party AI script unavailable'); continue; }
    member.vm = new EnemyScriptRuntime(script, { scripts, messages,
      condition(name, args) {
        if (name === 'missionmode') return missionMode;
        if (name === 'gotopponent') return gotOpponent(actor);
        if (name === 'helpwithtriggers') return helpWithTriggers(actor);
        if (name === 'canfightlikeajedi') return member.abilities.jedi;
        if (name === 'categoryis') return args[0]?.toLowerCase() === 'jedi' ? member.abilities.jedi : args[0]?.toLowerCase() === 'blaster' ? member.abilities.blaster : undefined;
        if (name === 'obstacleatend') return obstacleAtEnd(args[0]);
        if (name === 'playerrange') return Math.sqrt(distance2(position(actor), position(controlled.actor)));
        return undefined;
      },
      action(name, args, { firstTime, dt }) {
        const lower = args.map(value => value.toLowerCase());
        if (name === 'cannotdropin') { if (firstTime) actor.cannotDropIn = !lower.includes('false'); return true; }
        if (name === 'keepweaponout') { if (firstTime) actor.keepWeaponOut = !lower.includes('false'); return true; }
        if (name === 'idle') return false;
        if (name === 'snaptolocator') {
          if (firstTime || member.pendingSnap) {
            const name = lower.find(value => value.startsWith('name='))?.slice(5), locator = findLocator(name);
            if (!locator) { note(member, `Missing original snap locator ${name}`); return true; }
            const p = locator.position.slice(), floor = getFloor?.(p[0], p[1], p[2], actor);
            if (!lower.includes('dont_check_terrain')) {
              if (!Number.isFinite(floor)) { member.pendingSnap = true; note(member, `SnapToLocator terrain unavailable ${name}`); return undefined; }
              p[1] = floor;
            }
            setPosition(actor, p); actor.root.rotation.y = locator.heading; actor.authoredSpawn = { position: p.slice(), heading: locator.heading, locator: name };
            actor.companionLocator = locator.name;
            member.pendingSnap = false;
          }
          return true;
        }
        if (name === 'followplayer') {
          const parameter = Number(args.find(value => Number.isFinite(Number(value))) ?? 0), target = controlled.actor;
          const radius = Number.isFinite(target.moverRadius) ? target.moverRadius : target.config.values.radius;
          if (!Number.isFinite(parameter) || !Number.isFinite(radius)) return undefined;
          member.intent = { target: position(target), range: parameter + radius, speed: lower.includes('walk') ? 'walk' : 'run' }; return false;
        }
        if (name === 'gotolocator') {
          const specified = lower.find(value => value.startsWith('name='))?.slice(5) ?? (lower.indexOf('name') >= 0 ? lower[lower.indexOf('name') + 1] : actor.companionLocator);
          const locator = findLocator(specified); if (!locator) { note(member, `Missing original movement locator ${specified}`); return true; }
          if (firstTime) member.actionData = { wait: .01 };
          member.intent = { target: locator.position, range: 0, speed: 'run' };
          if (!firstTime && distance2(position(actor), locator.position) < (.1 + dt * member.velocity) ** 2) {
            member.intent = null;
            if (member.actionData.wait <= 0) return true;
            member.actionData.wait = Math.max(0, member.actionData.wait - dt);
          }
          return false;
        }
        return undefined;
      } });
  }
  function tag(name) {
    if (cutsceneLocked() || controlled.actor.dead || controlled.actor.disabled || controlled.actor.tagCooldown > 0)
      return { accepted: false, reason: 'Original tag input blocked by current player state' };
    const source = controlled, available = member => member && member !== source && !member.actor.dead && !member.actor.disabled &&
      !member.actor.playerControlled && !(member.actor.tagContextFlags & 2);
    let target = authoredMembers.find(member => member.name === name?.toLowerCase());
    if (!available(target)) target = slots.find(available);
    if (!target) return { accepted: false, reason: 'No available original party target' };
    const a = source.actor, b = target.actor, sourceHP = a.health?.current ?? a.hp, targetHP = b.health?.current ?? b.hp;
    if (!Number.isInteger(sourceHP) || !Number.isInteger(targetHP)) throw new Error('Original party health state unavailable');
    for (const key of ['playerIndex', 'playerControlled', 'coinpacket', 'inputPacket', 'bonusDeflectTimer']) [a[key], b[key]] = [b[key], a[key]];
    slots[a.playerIndex] = source; slots[b.playerIndex] = target;
    const sourceMaximum = a.health?.maximum ?? a.config.values.hit_points, targetMaximum = b.health?.maximum ?? b.config.values.hit_points;
    const hp = Math.max(1, Math.min(sourceHP, targetMaximum));
    if (a.health && b.health) {
      [a.health.spawnProtection, b.health.spawnProtection] = [b.health.spawnProtection, a.health.spawnProtection];
      a.health.current = sourceMaximum; b.health.current = hp;
    } else { a.hp = sourceMaximum; b.hp = hp; }
    b.tagCooldown = 1; b.hudIconTimer = 2; a.hudIconTimer = 0;
    controlled = target; onTransfer({ source: a, target: b, sourceName: source.name, targetName: target.name });
    return { accepted: true, source: a, target: b, sourceName: source.name, targetName: target.name };
  }
  function tick(dt) {
    if (!Number.isFinite(dt) || dt < 0 || dt > 1) throw new Error('Invalid original party timestep');
    for (const member of authoredMembers) {
      const actor = member.actor; actor.tagCooldown = Math.max(0, actor.tagCooldown - dt);
      if (member === controlled || actor.dead || actor.disabled || !member.vm) continue;
      member.intent = null; const result = member.vm.tick(dt);
      for (const blocked of result.blocked) note(member, `${blocked.kind}: ${blocked.name}`);
      actor.partyState = member.vm.state.name; member.velocity = 0;
      const intent = member.intent;
      if (intent) {
        const p = position(actor), dx = intent.target[0] - p[0], dz = intent.target[2] - p[2], length = Math.hypot(dx, dz), speed = actor.config.values[`${intent.speed}_speed`];
        if (length > intent.range && Number.isFinite(speed) && speed >= 0) {
          const step = Math.min(speed * dt, length - intent.range), next = [p[0] + dx / length * step, p[1], p[2] + dz / length * step];
          if (getLineOfSight?.(actor, { position: next, movementProbe: true }) === true) {
            const floor = getFloor?.(...next, actor);
            if (Number.isFinite(floor)) { next[1] = floor; setPosition(actor, next); actor.root.rotation.y = Math.atan2(dx, dz); member.velocity = step / (dt || 1); }
            else note(member, 'Original companion terrain unavailable');
          } else note(member, 'Original companion movement segment blocked/unbound');
        }
      }
      actor.aiClip = member.velocity ? member.intent.speed : 'idle';
    }
  }
  return { tag, tick, slots, members: authoredMembers, messages, report, get controlled() { return controlled; },
    next() { const index = authoredMembers.indexOf(controlled); for (let i = 1; i < authoredMembers.length; i++) { const target = authoredMembers[(index + i) % authoredMembers.length]; if (!target.actor.dead && !target.actor.disabled) return tag(target.name); } return { accepted: false, reason: 'No available original party target' }; } };
}
